import React from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import { PROFILE_AXES } from "../../ShowStylePage/profile";

export const SidebarWidgets: React.FC = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  const name = watch("name");
  const description = watch("description") || "";
  const minOg = watch("minOg");
  const minIbu = watch("minIbu");
  const maxIbu = watch("maxIbu");
  const minColor = watch("minColor");
  const minServingTemp = watch("minServingTemp");
  const maxServingTemp = watch("maxServingTemp");
  const profile = watch("profile");
  const hasProfile = PROFILE_AXES.some((axis) => Boolean(profile?.[axis]));
  const entryAdditionalFields = watch("settings.entryAdditionalFields") || [];

  const guidelines = watch("guidelines") || {};
  const commercialExamples = guidelines.commercialExamples;

  const hasExamples = Array.isArray(commercialExamples)
    ? commercialExamples.length > 0
    : typeof commercialExamples === "string"
    ? commercialExamples.trim().length > 0
    : false;

  interface Suggestion {
    id: string;
    title: string;
    desc: string;
    icon: string;
    bgColor: string;
    iconColor: string;
  }

  const suggestions: Suggestion[] = [];

  if (!hasExamples) {
    suggestions.push({
      id: "examples",
      title: t(
        "Pages:StyleCreator.Suggestions.Examples.Title",
        "Add commercial examples",
      ),
      desc: t(
        "Pages:StyleCreator.Suggestions.Examples.Desc",
        "Including examples helps judges understand the style better.",
      ),
      icon: "icon-[ph--lightbulb]",
      bgColor: "bg-[#ffdcc3]",
      iconColor: "text-[#904d00]",
    });
  }

  const minIbuNum =
    minIbu !== undefined && minIbu !== null && minIbu !== "" && !isNaN(Number(minIbu))
      ? Number(minIbu)
      : null;
  const maxIbuNum =
    maxIbu !== undefined && maxIbu !== null && maxIbu !== "" && !isNaN(Number(maxIbu))
      ? Number(maxIbu)
      : null;

  if (minIbuNum === null && maxIbuNum === null) {
    suggestions.push({
      id: "ibu_empty",
      title: t(
        "Pages:StyleCreator.Suggestions.IbuEmpty.Title",
        "No IBU range specified",
      ),
      desc: t(
        "Pages:StyleCreator.Suggestions.IbuEmpty.Desc",
        "Specify the expected bitterness level for this style.",
      ),
      icon: "icon-[ph--warning]",
      bgColor: "bg-[#ffdcc3]",
      iconColor: "text-[#904d00]",
    });
  } else if (
    minIbuNum !== null &&
    maxIbuNum !== null &&
    maxIbuNum - minIbuNum > 40
  ) {
    suggestions.push({
      id: "ibu_wide",
      title: t(
        "Pages:StyleCreator.Suggestions.Ibu.Title",
        "Review IBU range",
      ),
      desc: t(
        "Pages:StyleCreator.Suggestions.Ibu.Desc",
        "The IBU range is quite wide. Consider narrowing it.",
      ),
      icon: "icon-[ph--warning]",
      bgColor: "bg-[#ffdcc3]",
      iconColor: "text-[#904d00]",
    });
  }

  const hasServingTemp =
    (minServingTemp !== undefined && minServingTemp !== null && minServingTemp !== "") ||
    (maxServingTemp !== undefined && maxServingTemp !== null && maxServingTemp !== "");

  if (!hasServingTemp) {
    suggestions.push({
      id: "temp",
      title: t(
        "Pages:StyleCreator.Suggestions.Temp.Title",
        "No serving temperature",
      ),
      desc: t(
        "Pages:StyleCreator.Suggestions.Temp.Desc",
        "We couldn't detect serving temperature in the source.",
      ),
      icon: "icon-[ph--thermometer]",
      bgColor: "bg-[#ffdcc3]",
      iconColor: "text-[#904d00]",
    });
  }

  const calculateCompleteness = () => {
    let score = 0;
    if (name) score += 20;
    if (description) score += 20;
    if (minOg || minIbu || minColor) score += 20;
    if (hasProfile) score += 20;
    if (entryAdditionalFields.length > 0) score += 20;
    return score;
  };

  const completeness = calculateCompleteness();

  return (
    <>
      {/* Completeness SVG circular progress bar */}
      <section className="bg-white/85 backdrop-blur-md border border-[#e5e0d8] shadow-sm transition-all duration-200 hover:border-[#8b4513]/40 p-6 rounded-xl space-y-6">
        <h3 className="text-xs font-bold text-[#877369] uppercase tracking-wider">
          {t("Pages:StyleCreator.Sidebar.Completeness", "Completeness")}
        </h3>

        <div className="flex items-center gap-4">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <svg viewBox="0 0 64 64" className="w-full h-full">
              <g transform="rotate(-90 32 32)">
                <circle
                  cx="32"
                  cy="32"
                  fill="transparent"
                  r="28"
                  stroke="#f0dfd6"
                  strokeWidth="5"
                />
                <circle
                  cx="32"
                  cy="32"
                  fill="transparent"
                  r="28"
                  stroke="#fe932c"
                  strokeDasharray="175.9"
                  strokeDashoffset={(
                    175.9 -
                    (175.9 * completeness) / 100
                  ).toFixed(1)}
                  strokeWidth="5"
                  className="transition-all duration-300"
                />
              </g>
            </svg>
            <span className="absolute text-sm font-bold text-[#221a14]">
              {completeness}%
            </span>
          </div>
          <p className="text-xs text-[#54433a] leading-tight">
            {completeness >= 80
              ? t(
                  "Pages:StyleCreator.Sidebar.CompletenessHigh",
                  "Excellent! Form details are fully complete.",
                )
              : t(
                  "Pages:StyleCreator.Sidebar.CompletenessMid",
                  "Good progress! A few fields left to complete.",
                )}
          </p>
        </div>

        <ul className="space-y-3 text-xs font-medium">
          <li
            className={classNames(
              "flex items-center gap-2",
              name ? "text-[#034b32]" : "text-[#877369]",
            )}
          >
            <span
              className={classNames(
                "text-sm",
                name ? "icon-[ph--check-circle-fill]" : "icon-[ph--circle]",
              )}
            />
            {t("Pages:StyleCreator.Sidebar.Check.Name", "Name")}
          </li>
          <li
            className={classNames(
              "flex items-center gap-2",
              description ? "text-[#034b32]" : "text-[#877369]",
            )}
          >
            <span
              className={classNames(
                "text-sm",
                description ? "icon-[ph--check-circle-fill]" : "icon-[ph--circle]",
              )}
            />
            {t("Pages:StyleCreator.Sidebar.Check.Desc", "Description")}
          </li>
          <li
            className={classNames(
              "flex items-center gap-2",
              minOg || minIbu ? "text-[#034b32]" : "text-[#877369]",
            )}
          >
            <span
              className={classNames(
                "text-sm",
                minOg || minIbu ? "icon-[ph--check-circle-fill]" : "icon-[ph--circle]",
              )}
            />
            {t("Pages:StyleCreator.Sidebar.Check.Params", "Parameters")}
          </li>
          <li
            className={classNames(
              "flex items-center gap-2",
              hasProfile ? "text-[#034b32]" : "text-[#877369]",
            )}
          >
            <span
              className={classNames(
                "text-sm",
                hasProfile ? "icon-[ph--check-circle-fill]" : "icon-[ph--circle]",
              )}
            />
            {t(
              "Pages:StyleCreator.Sidebar.Check.Sensory",
              "Sensory sections",
            )}
          </li>
          <li
            className={classNames(
              "flex items-center gap-2",
              entryAdditionalFields.length > 0 ? "text-[#034b32]" : "text-[#877369]",
            )}
          >
            <span
              className={classNames(
                "text-sm",
                entryAdditionalFields.length > 0
                  ? "icon-[ph--check-circle-fill]"
                  : "icon-[ph--circle]",
              )}
            />
            {t("Pages:StyleCreator.Sidebar.Check.Entry", "Entry fields")}
          </li>
        </ul>
      </section>

      {/* Suggestions List */}
      <section className="bg-white/85 backdrop-blur-md border border-[#e5e0d8] shadow-sm transition-all duration-200 hover:border-[#8b4513]/40 p-6 rounded-xl space-y-6">
        <h3 className="text-xs font-bold text-[#877369] uppercase tracking-wider">
          {t("Pages:StyleCreator.Sidebar.Suggestions", "Suggestions")}
        </h3>

        <div className="space-y-4">
          {suggestions.length > 0 ? (
            suggestions.map((suggestion) => (
              <div key={suggestion.id} className="flex gap-3 items-start text-left min-w-0">
                <div
                  className={classNames(
                    "w-8 h-8 rounded flex-shrink-0 flex items-center justify-center",
                    suggestion.bgColor,
                    suggestion.iconColor,
                  )}
                >
                  <span className={classNames(suggestion.icon, "text-lg")} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-[#221a14]">{suggestion.title}</div>
                  <p className="text-[10px] text-[#54433a] leading-normal mt-0.5 font-sans">
                    {suggestion.desc}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex gap-3 items-start text-left min-w-0">
              <div className="w-8 h-8 rounded bg-[#b1f0ce]/40 flex-shrink-0 flex items-center justify-center text-[#0e5138]">
                <span className="icon-[ph--check-circle-fill] text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-[#0e5138]">
                  {t(
                    "Pages:StyleCreator.Suggestions.AllPassed.Title",
                    "All checks passed",
                  )}
                </div>
                <p className="text-[10px] text-[#0e5138]/90 leading-normal mt-0.5 font-sans">
                  {t(
                    "Pages:StyleCreator.Suggestions.AllPassed.Desc",
                    "Your style guidelines are complete and well structured!",
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {suggestions.length > 0 && (
          <Button
            htmlType="button"
            type="base"
            variant="ghost"
            className="w-full text-center text-xs font-bold text-[#6c2f00] hover:underline p-0"
          >
            {t(
              "Pages:StyleCreator.Sidebar.ViewAllSuggestions",
              "View all suggestions",
            )}
          </Button>
        )}
      </section>

      {/* Automation Log info */}
      <section className="bg-white/85 backdrop-blur-md border border-[#e5e0d8] shadow-sm transition-all duration-200 hover:border-[#8b4513]/40 p-6 rounded-xl space-y-4">
        <div className="flex items-center gap-2">
          <span className="icon-[ph--info] text-[#6c2f00] text-lg" />
          <h3 className="text-xs font-bold text-[#877369] uppercase tracking-wider">
            {t("Pages:StyleCreator.Sidebar.Automation", "Automation")}
          </h3>
        </div>

        <div className="space-y-2 text-xs font-medium">
          <div className="flex items-center justify-between">
            <span className="text-[#54433a]">
              {t(
                "Pages:StyleCreator.Sidebar.Auto.Processed",
                "Source processed",
              )}
            </span>
            <span className="icon-[ph--check] text-xs text-[#034b32]" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#54433a]">
              {t(
                "Pages:StyleCreator.Sidebar.Auto.Filled",
                "18 fields auto-filled",
              )}
            </span>
            <span className="icon-[ph--check] text-xs text-[#034b32]" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#54433a]">
              {t(
                "Pages:StyleCreator.Sidebar.Auto.Flagged",
                "3 need review",
              )}
            </span>
            <span className="icon-[ph--warning] text-xs text-[#904d00]" />
          </div>
        </div>

        <Button
          htmlType="button"
          type="base"
          variant="ghost"
          className="w-full text-center text-xs font-bold text-[#6c2f00] hover:underline p-0 pt-2"
        >
          {t("Pages:StyleCreator.Sidebar.ViewLogs", "View logs")}
        </Button>
      </section>
    </>
  );
};
